import { z } from "zod"
import { branchIdSchema, correlationIdSchema, tenantIdSchema } from "./identifiers.js"

const idSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/)
const text = (maximum: number) => z.string().min(1).max(maximum)

export const customerWorkspaceIssueV1Schema = z.object({
  key: text(64),
  severity: z.enum(["blocker", "warning"]),
  title: text(120),
  detail: text(300),
  destination: text(64),
}).strict()

const contactSchema = z.object({
  contactId: idSchema,
  displayName: text(160),
  role: text(100),
  email: z.string().email().nullable(),
  phone: text(32).nullable(),
  isPrimary: z.boolean(),
  estimateApprover: z.boolean(),
}).strict()

const locationSchema = z.object({
  locationId: idSchema,
  label: text(120),
  address: text(300),
  serviceability: z.enum(["confirmed", "manual_review", "outside"]),
  estimatable: z.boolean(),
}).strict()

const branchAccessSchema = z.object({
  branchId: branchIdSchema,
  branchName: text(120),
  kind: z.enum(["owner", "grant"]),
  operationalAccess: z.boolean(),
  billingAccess: z.boolean(),
  state: z.enum(["Active", "Requested", "Rejected", "Revoked"]),
}).strict()

const activitySchema = z.object({
  activityId: idSchema,
  occurredAt: z.string().datetime(),
  actorDisplayName: text(160),
  action: text(160),
  summary: text(300),
  correlationId: correlationIdSchema,
}).strict()

export const customerWorkspaceReadyV1Schema = z.object({
  state: z.literal("ready"),
  tenantId: tenantIdSchema,
  customerId: idSchema,
  version: z.number().int().positive(),
  displayName: text(160),
  customerType: z.enum(["residential", "commercial", "property_manager"]),
  status: z.enum(["Active", "On Hold", "Inactive", "Archived"]),
  owningBranchId: branchIdSchema,
  owningBranchName: text(120),
  accountOwner: text(160).nullable(),
  updatedAt: z.string().datetime(),
  contacts: z.array(contactSchema).max(50),
  locations: z.array(locationSchema).max(100),
  branchAccess: z.array(branchAccessSchema).max(100),
  readiness: z.object({
    activation: z.array(customerWorkspaceIssueV1Schema).max(25),
    estimate: z.array(customerWorkspaceIssueV1Schema).max(25),
    scheduling: z.array(customerWorkspaceIssueV1Schema).max(25),
    invoicing: z.array(customerWorkspaceIssueV1Schema).max(25),
  }).strict(),
  activity: z.array(activitySchema).max(50),
}).strict()

export const customerWorkspaceV1Schema = z.discriminatedUnion("state", [
  customerWorkspaceReadyV1Schema,
  z.object({ state: z.literal("loading") }).strict(),
  z.object({ state: z.literal("empty"), title: text(120), detail: text(300) }).strict(),
  z.object({ state: z.literal("error"), title: text(120), detail: text(300), retryable: z.boolean() }).strict(),
])

export type CustomerWorkspaceIssueV1 = z.infer<typeof customerWorkspaceIssueV1Schema>
export type CustomerWorkspaceReadyV1 = z.infer<typeof customerWorkspaceReadyV1Schema>
export type CustomerWorkspaceV1 = z.infer<typeof customerWorkspaceV1Schema>
